import { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useHistory, useLocation } from 'react-router-dom';
import HearingIcon from '@mui/icons-material/Hearing';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import axiosConn from '../../axiosConn';
import { AuthContext } from '../../context/AuthContext';
import GenericHelmet from '../../components/genericHelmet/GenericHelmet';
import getShareUrl from '../../utils/getShareUrl';
import {
  EMPTY_RADIO_QUEUE,
  getActiveRadioQueue,
  getActiveRadioTracks,
  HITLAB_RADIO_ROUTE,
  mergeRadioCatalogItems,
  normalizeRadioCatalogResponseItems,
  RADIO_QUEUE_KINDS,
} from './hitLabRadioTracks';
import {
  buildRadioListenSessionId,
  getAudioListenSnapshot,
  RADIO_LISTEN_HEARTBEAT_MS,
  sendRadioListenEvent,
} from './radioListenEvents';
import { getHitLabApiMessage } from './hitLabUtils';
import HitLabRadioFeedbackSheet from './HitLabRadioFeedbackSheet';
import HitLabRadioJgkSupportSheet from './HitLabRadioJgkSupportSheet';
import RadioListenerPresenceRow, {
  getRadioListenerPresenceCount,
} from './RadioListenerPresenceRow';
import useJgRadioLiveSignals from './useJgRadioLiveSignals';
import './hitLab.css';

const RADIO_PLAY_MIN_SECONDS = 30;
const RADIO_PLAY_MIN_PERCENT = 25;
const RADIO_PUBLIC_URL = 'https://www.joegalaxy.net/hitlab/radio';
const ARTIST_INVITE_CLAIM_INTENT_KEY = 'hitlabRadioArtistInviteClaimIntent:v1';
const ARTIST_INVITE_CLAIM_INTENT_MAX_MS = 30 * 60 * 1000;
const JG_LOGO_MARK_URL =
  'https://res.cloudinary.com/joegalaxy/image/upload/q_auto/v1664565843/Images/JOEGALAXYDopeGalaxyLogooriginal_tecmer.png';

const buildRadioTrackUrl = (trackId) => {
  const cleanTrackId = String(trackId || '').trim();
  return cleanTrackId
    ? `${RADIO_PUBLIC_URL}?track=${encodeURIComponent(cleanTrackId)}`
    : RADIO_PUBLIC_URL;
};

const isNativeShareCancel = (err) => {
  const name = String(err?.name || '').toLowerCase();
  const message = String(err?.message || '').toLowerCase();
  return name === 'aborterror' || message.includes('cancel');
};

const formatTime = (value) => {
  const seconds = Number(value);
  if (!Number.isFinite(seconds) || seconds < 0) return '0:00';
  const minutes = Math.floor(seconds / 60);
  const remainder = Math.floor(seconds % 60);
  return `${minutes}:${String(remainder).padStart(2, '0')}`;
};

const clampIndex = (index, length) => {
  if (!length) return 0;
  const next = Number(index);
  if (!Number.isFinite(next)) return 0;
  return ((Math.trunc(next) % length) + length) % length;
};

const getLocalUtcDayKey = () => new Date().toISOString().slice(0, 10);

const normalizePlayCount = (value) => {
  const count = Number(value);
  if (!Number.isFinite(count) || count < 0) return 0;
  return Math.floor(count);
};

const normalizePlayMetrics = (tracks = []) =>
  (Array.isArray(tracks) ? tracks : []).reduce((acc, item) => {
    const trackId = String(item?.trackId || '').trim();
    if (trackId) acc[trackId] = normalizePlayCount(item?.playCount);
    return acc;
  }, {});

const getTrackPlayCount = (metrics, trackId) =>
  normalizePlayCount(metrics?.[trackId]);

const buildPlaySessionId = (trackId) =>
  [
    'hitlab-radio',
    String(trackId || 'track').replace(/[^a-z0-9_-]/gi, '').slice(0, 48),
    Date.now(),
    Math.random().toString(36).slice(2, 8),
  ].join(':');

const findTrackQueueIndex = (queue = [], trackId) =>
  (Array.isArray(queue) ? queue : []).findIndex(
    (item) =>
      item?.kind === RADIO_QUEUE_KINDS.TRACK &&
      String(item.id || item.trackId || '') === String(trackId || ''),
  );

const readArtistInviteClaimIntent = () => {
  try {
    const parsed = JSON.parse(
      window.localStorage.getItem(ARTIST_INVITE_CLAIM_INTENT_KEY) || 'null',
    );
    if (!parsed || typeof parsed !== 'object') return null;
    const createdAt = Number(parsed.createdAt || 0);
    if (!createdAt || Date.now() - createdAt > ARTIST_INVITE_CLAIM_INTENT_MAX_MS) {
      window.localStorage.removeItem(ARTIST_INVITE_CLAIM_INTENT_KEY);
      return null;
    }
    return parsed;
  } catch (err) {
    return null;
  }
};

const writeArtistInviteClaimIntent = ({ trackId, token }) => {
  try {
    window.localStorage.setItem(
      ARTIST_INVITE_CLAIM_INTENT_KEY,
      JSON.stringify({
        trackId: String(trackId || '').trim(),
        token: String(token || '').trim(),
        createdAt: Date.now(),
      }),
    );
  } catch (err) {
    // The manual claim button remains available if storage is unavailable.
  }
};

const clearArtistInviteClaimIntent = () => {
  try {
    window.localStorage.removeItem(ARTIST_INVITE_CLAIM_INTENT_KEY);
  } catch (err) {
    // Ignore storage failures.
  }
};

export default function HitLabRadio({ queue = EMPTY_RADIO_QUEUE }) {
  const audioRef = useRef(null);
  const pendingAutoPlayRef = useRef(false);
  const feedbackCtaRef = useRef(null);
  const playSessionIdRef = useRef('');
  const playPostKeysRef = useRef(new Set());
  const listenSessionRef = useRef(null);
  const listenHeartbeatTimerRef = useRef(null);
  const durationRef = useRef(0);
  const appliedQueryTrackRef = useRef('');
  const previousPanelTrackIdRef = useRef('');
  const history = useHistory();
  const location = useLocation();
  const { player } = useContext(AuthContext);
  const [catalogQueue, setCatalogQueue] = useState(null);
  const resolvedQueue = catalogQueue || queue;
  const activeQueue = useMemo(
    () => getActiveRadioQueue(resolvedQueue),
    [resolvedQueue],
  );
  const activeTrackCount = useMemo(
    () => getActiveRadioTracks(resolvedQueue).length,
    [resolvedQueue],
  );
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isPlaying, setIsPlaying] = useState(false);
  const [isCompact, setIsCompact] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [duration, setDuration] = useState(0);
  const [elapsed, setElapsed] = useState(0);
  const [feedbackOpen, setFeedbackOpen] = useState(false);
  const [supportMode, setSupportMode] = useState(null);
  const [trackPlayMetrics, setTrackPlayMetrics] = useState({});
  const [playCountInfoOpen, setPlayCountInfoOpen] = useState(false);
  const [shareMessage, setShareMessage] = useState('');
  const [shareError, setShareError] = useState('');
  const [artistInviteStatus, setArtistInviteStatus] = useState(null);
  const [artistInviteLoading, setArtistInviteLoading] = useState(false);
  const [artistInviteError, setArtistInviteError] = useState('');
  const [artistClaimSaving, setArtistClaimSaving] = useState(false);
  const [artistClaimResult, setArtistClaimResult] = useState(null);

  const currentItem = activeQueue[clampIndex(currentIndex, activeQueue.length)] || null;
  const isTrack = currentItem?.kind === RADIO_QUEUE_KINDS.TRACK;
  const isBumper = currentItem?.kind === RADIO_QUEUE_KINDS.BUMPER;
  const feedbackEligible = isTrack && currentItem?.feedbackEligible !== false;
  const progress = duration > 0 ? Math.min(100, (elapsed / duration) * 100) : 0;
  const currentRoute = `${location.pathname || HITLAB_RADIO_ROUTE}${location.search || ''}`;
  const feedbackLoginHref = `/login?from=${encodeURIComponent(currentRoute)}`;
  const queryTrackId = useMemo(() => {
    const params = new URLSearchParams(location.search || '');
    return String(params.get('track') || '').trim();
  }, [location.search]);
  const artistInviteToken = useMemo(() => {
    const params = new URLSearchParams(location.search || '');
    return String(params.get('artistInvite') || '').trim();
  }, [location.search]);
  const queryTrackIndex = useMemo(
    () => (queryTrackId ? findTrackQueueIndex(activeQueue, queryTrackId) : -1),
    [activeQueue, queryTrackId],
  );
  const queryTrackResolved = Boolean(queryTrackId && queryTrackIndex >= 0);
  const queryTrackMissing = Boolean(
    queryTrackId && activeQueue.length && queryTrackIndex < 0,
  );
  const currentTrackId = isTrack ? String(currentItem?.id || currentItem?.trackId || '') : '';
  const isFocusedTrackView = Boolean(
    queryTrackResolved && currentTrackId && currentTrackId === queryTrackId,
  );
  const currentTrackTitle = isTrack ? currentItem.songTitle : 'Joe Galaxy Radio';
  const currentArtistName = isTrack ? currentItem.artistName : 'JG Universe';
  const trackDestinationUrl = buildRadioTrackUrl(currentTrackId);
  const trackShareUrl = currentTrackId ? getShareUrl('radio', currentTrackId) : '';
  const showArtistInvitePanel = Boolean(
    artistInviteToken && queryTrackId && isFocusedTrackView,
  );
  const artistClaimPlayerId = String(artistClaimResult?.playerId || '').trim();
  const showArtistClaimSuccess = Boolean(
    artistClaimResult?.connected || artistClaimResult?.next === 'connected',
  );
  const showArtistClaimPending =
    !showArtistClaimSuccess &&
    (artistInviteStatus?.status === 'claimed' || artistClaimResult?.claimReceived);
  const focusedDescription = isTrack
    ? `Hear ${currentTrackTitle} by ${currentArtistName} in Joe Galaxy Radio Rotation inside JG Universe. Listen, give feedback, and support with JGK where available.`
    : 'Hear selected records in Joe Galaxy Radio Rotation inside JG Universe.';
  const radioPageMeta = isFocusedTrackView
    ? {
        title: `${currentTrackTitle} by ${currentArtistName} | Joe Galaxy Radio`,
        description: focusedDescription,
        canonicalUrl: trackDestinationUrl,
        ogUrl: trackDestinationUrl,
        ogImage: currentItem.coverArtUrl,
        twitterImage: currentItem.coverArtUrl,
      }
    : {
        title: 'Joe Galaxy Radio | JG Universe',
        description: 'Hear selected records in Joe Galaxy Radio Rotation inside JG Universe.',
        canonicalUrl: RADIO_PUBLIC_URL,
        ogUrl: RADIO_PUBLIC_URL,
        ogImage: currentItem?.coverArtUrl,
        twitterImage: currentItem?.coverArtUrl,
    };
  const fetchRadioCatalog = useCallback(async () => {
    const response = player?.token
      ? await axiosConn.get('hitlab/radio/catalog', {
          headers: {
            Authorization: `Bearer ${player.token}`,
          },
        })
      : await axiosConn.get('hitlab/radio/catalog');
    const items = normalizeRadioCatalogResponseItems(response);
    return mergeRadioCatalogItems(items);
  }, [player?.token]);

  const refreshRadioCatalog = useCallback(async () => {
    try {
      const nextQueue = await fetchRadioCatalog();
      setCatalogQueue(nextQueue);
    } catch (err) {
      setCatalogQueue((current) =>
        current && current.length ? current : EMPTY_RADIO_QUEUE,
      );
    }
  }, [fetchRadioCatalog]);

  const liveSignalCapsule = useMemo(() => {
    if (!isTrack || !currentTrackId) return null;
    return {
      kind: RADIO_QUEUE_KINDS.TRACK,
      trackId: currentTrackId,
      title: currentTrackTitle,
      artistName: currentArtistName,
      linkedArtistPlayerId: String(
        currentItem?.linkedArtistPlayerId || currentItem?.artistPlayerId || '',
      ).trim(),
    };
  }, [
    currentArtistName,
    currentItem?.artistPlayerId,
    currentItem?.linkedArtistPlayerId,
    currentTrackId,
    currentTrackTitle,
    isTrack,
  ]);
  const radioLiveSignals = useJgRadioLiveSignals({
    capsule: liveSignalCapsule,
    isPlaying,
    enabled: Boolean(activeQueue.length),
    onRefreshCatalog: refreshRadioCatalog,
  });
  const mergedTrackPlayMetrics = useMemo(
    () => ({
      ...trackPlayMetrics,
      ...(radioLiveSignals.playCountsByTrackId || {}),
    }),
    [radioLiveSignals.playCountsByTrackId, trackPlayMetrics],
  );
  const currentTrackPlayCount = isTrack
    ? getTrackPlayCount(mergedTrackPlayMetrics, currentItem?.id)
    : 0;
  const linkedArtistPresence = radioLiveSignals.linkedArtistPresence || {};
  const artistPresenceClass = linkedArtistPresence.isInRoom
    ? 'is-in-room'
    : linkedArtistPresence.isOnline
      ? 'is-online'
      : '';
  const artistPresenceLabel = linkedArtistPresence.isInRoom
    ? 'In JG Radio now'
    : linkedArtistPresence.isOnline
      ? 'Online in JG Universe'
      : '';
  const stationListenerCount = getRadioListenerPresenceCount(
    radioLiveSignals.stationListenerPresence,
  );
  const currentRecordListenerCount = isTrack
    ? getRadioListenerPresenceCount(radioLiveSignals.listenerPresence)
    : 0;
  const showLiveRoomState = Boolean(
    radioLiveSignals.movementText ||
      stationListenerCount > 0 ||
      currentRecordListenerCount > 0,
  );

  useEffect(() => {
    let isMounted = true;
    setArtistClaimResult(null);
    setArtistInviteError('');
    if (!artistInviteToken || !queryTrackId) {
      setArtistInviteStatus(null);
      setArtistInviteLoading(false);
      return () => {
        isMounted = false;
      };
    }

    const loadArtistInviteStatus = async () => {
      setArtistInviteLoading(true);
      try {
        const response = await axiosConn.get('hitlab/radio/artist-invite/status', {
          params: {
            trackId: queryTrackId,
            token: artistInviteToken,
          },
        });
        if (!isMounted) return;
        setArtistInviteStatus(response?.data?.data || null);
      } catch (err) {
        if (!isMounted) return;
        setArtistInviteStatus({
          available: false,
          reason: 'invalid',
          message:
            'This artist invite is not available. You can still listen to the record.',
        });
      } finally {
        if (isMounted) setArtistInviteLoading(false);
      }
    };

    void loadArtistInviteStatus();
    return () => {
      isMounted = false;
    };
  }, [artistInviteToken, queryTrackId]);

  useEffect(() => {
    let isMounted = true;
    const loadRadioMetrics = async () => {
      try {
        const response = await axiosConn.get('hitlab/radio/metrics');
        if (!isMounted) return;
        const tracks = response?.data?.data?.tracks;
        if (Array.isArray(tracks)) {
          setTrackPlayMetrics(normalizePlayMetrics(tracks));
        }
      } catch (err) {
        // Metrics are supplemental; playback stays usable with local zero counts.
      }
    };

    void loadRadioMetrics();
    return () => {
      isMounted = false;
    };
  }, []);

  useEffect(() => {
    let isMounted = true;
    const loadRadioCatalog = async () => {
      try {
        const nextQueue = await fetchRadioCatalog();
        if (isMounted) setCatalogQueue(nextQueue);
      } catch (err) {
        if (isMounted) {
          setCatalogQueue((current) =>
            current && current.length ? current : EMPTY_RADIO_QUEUE,
          );
        }
      }
    };

    void loadRadioCatalog();
    return () => {
      isMounted = false;
    };
  }, [fetchRadioCatalog, queue]);

  useEffect(() => {
    durationRef.current = duration;
  }, [duration]);

  const clearListenHeartbeatTimer = useCallback(() => {
    if (listenHeartbeatTimerRef.current) {
      window.clearInterval(listenHeartbeatTimerRef.current);
      listenHeartbeatTimerRef.current = null;
    }
  }, []);

  const postListenEvent = useCallback(
    (eventType, sessionOverride = null) => {
      const session = sessionOverride || listenSessionRef.current;
      if (!session?.sessionId || !session?.trackId) return;
      sendRadioListenEvent({
        eventType,
        sessionId: session.sessionId,
        trackId: session.trackId,
        source: session.source,
        playerToken: player?.token,
        ...getAudioListenSnapshot(audioRef.current, durationRef.current),
      });
    },
    [player?.token],
  );

  const endListenSession = useCallback(() => {
    const session = listenSessionRef.current;
    if (!session) return;
    clearListenHeartbeatTimer();
    postListenEvent('end', session);
    listenSessionRef.current = null;
  }, [clearListenHeartbeatTimer, postListenEvent]);

  const startListenSession = useCallback(() => {
    const trackId = String(currentItem?.id || currentItem?.trackId || '').trim();
    if (!trackId) return;

    if (listenSessionRef.current?.trackId === trackId) return;
    if (listenSessionRef.current) endListenSession();

    const session = {
      sessionId: buildRadioListenSessionId('hitlab-radio-page', trackId),
      trackId,
      source: 'hitlab_radio_page',
    };
    listenSessionRef.current = session;
    postListenEvent('start', session);
    clearListenHeartbeatTimer();
    listenHeartbeatTimerRef.current = window.setInterval(() => {
      postListenEvent('heartbeat');
    }, RADIO_LISTEN_HEARTBEAT_MS);
  }, [clearListenHeartbeatTimer, currentItem, endListenSession, postListenEvent]);

  useEffect(() => {
    const handlePageHide = () => {
      endListenSession();
    };
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'hidden') endListenSession();
    };
    window.addEventListener('pagehide', handlePageHide);
    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => {
      window.removeEventListener('pagehide', handlePageHide);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [endListenSession]);

  const resetAudioState = useCallback(() => {
    const audio = audioRef.current;
    if (!audio) return;
    try {
      audio.pause();
      audio.currentTime = 0;
      audio.load?.();
    } catch (err) {
      // Browser media state can reject seeks; keep the UI usable.
    }
  }, []);

  const playCurrent = useCallback(async () => {
    const audio = audioRef.current;
    if (!audio || !currentItem) return false;
    setErrorMessage('');
    setIsLoading(true);
    try {
      const result = audio.play();
      if (result && typeof result.then === 'function') {
        await result;
      }
      setIsPlaying(true);
      setIsLoading(false);
      startListenSession();
      return true;
    } catch (err) {
      setIsPlaying(false);
      setIsLoading(false);
      setErrorMessage('This audio item could not start. Try the next item.');
      return false;
    }
  }, [currentItem, startListenSession]);

  const goToIndex = useCallback(
    (nextIndex, { keepPlaying = false } = {}) => {
      if (!activeQueue.length) return;
      endListenSession();
      pendingAutoPlayRef.current = keepPlaying;
      setCurrentIndex(clampIndex(nextIndex, activeQueue.length));
    },
    [activeQueue.length, endListenSession],
  );

  const goNext = useCallback(
    ({ keepPlaying = isPlaying } = {}) => {
      goToIndex(currentIndex + 1, { keepPlaying });
    },
    [currentIndex, goToIndex, isPlaying],
  );

  const goPrevious = useCallback(() => {
    goToIndex(currentIndex - 1, { keepPlaying: isPlaying });
  }, [currentIndex, goToIndex, isPlaying]);

  useEffect(() => {
    setCurrentIndex((prev) => clampIndex(prev, activeQueue.length));
  }, [activeQueue.length]);

  useEffect(() => {
    if (!queryTrackId || !activeQueue.length) return;
    if (appliedQueryTrackRef.current === queryTrackId) return;
    const nextIndex = findTrackQueueIndex(activeQueue, queryTrackId);
    if (nextIndex < 0) return;
    appliedQueryTrackRef.current = queryTrackId;
    pendingAutoPlayRef.current = false;
    setCurrentIndex(nextIndex);
  }, [activeQueue, queryTrackId]);

  useEffect(() => {
    endListenSession();
    resetAudioState();
    playSessionIdRef.current = buildPlaySessionId(currentItem?.id);
    setElapsed(0);
    setDuration(0);
    setErrorMessage('');
    setIsLoading(false);
    setIsPlaying(false);
    if (pendingAutoPlayRef.current) {
      pendingAutoPlayRef.current = false;
      window.setTimeout(() => {
        void playCurrent();
      }, 0);
    }
  }, [currentItem?.id, endListenSession, playCurrent, resetAudioState]);

  useEffect(
    () => () => {
      endListenSession();
      resetAudioState();
      clearListenHeartbeatTimer();
    },
    [clearListenHeartbeatTimer, endListenSession, resetAudioState],
  );

  const togglePlayback = () => {
    const audio = audioRef.current;
    if (!audio) return;
    if (isPlaying) {
      endListenSession();
      audio.pause();
      setIsPlaying(false);
      return;
    }
    void playCurrent();
  };

  const handleLoadedMetadata = () => {
    const nextDuration = Number(audioRef.current?.duration);
    setDuration(Number.isFinite(nextDuration) ? nextDuration : 0);
    setIsLoading(false);
  };

  const recordQualifiedPlayIfReady = useCallback(
    async ({ nextElapsed, nextDuration }) => {
      if (!player?.token || !currentItem || !isTrack) return;

      const listenedSeconds = Math.floor(Math.max(0, Number(nextElapsed) || 0));
      const listenPercent =
        nextDuration > 0
          ? Math.min(100, Math.round((listenedSeconds / nextDuration) * 100))
          : 0;

      if (
        listenedSeconds < RADIO_PLAY_MIN_SECONDS &&
        listenPercent < RADIO_PLAY_MIN_PERCENT
      ) {
        return;
      }

      const localDayKey = getLocalUtcDayKey();
      const postKey = `${currentItem.id}:${localDayKey}`;
      if (playPostKeysRef.current.has(postKey)) return;
      playPostKeysRef.current.add(postKey);

      try {
        const response = await axiosConn.post(
          'hitlab/radio/play',
          {
            trackId: currentItem.id,
            listenSession: {
              listenedSeconds,
              listenPercent,
            },
            clientPlaySessionId: playSessionIdRef.current,
          },
          {
            headers: {
              Authorization: `Bearer ${player.token}`,
            },
          },
        );
        const play = response?.data?.data?.play;
        if (play?.trackId) {
          setTrackPlayMetrics((current) => ({
            ...current,
            [play.trackId]: normalizePlayCount(play.playCount),
          }));
        }
      } catch (err) {
        // Play-count telemetry must never interrupt Radio playback.
      }
    },
    [currentItem, isTrack, player?.token],
  );

  const handleTimeUpdate = () => {
    const nextElapsed = Number(audioRef.current?.currentTime);
    const safeElapsed = Number.isFinite(nextElapsed) ? nextElapsed : 0;
    const nextDuration = Number(audioRef.current?.duration);
    const safeDuration = Number.isFinite(nextDuration) ? nextDuration : duration;
    setElapsed(safeElapsed);
    if (isPlaying) {
      void recordQualifiedPlayIfReady({
        nextElapsed: safeElapsed,
        nextDuration: safeDuration,
      });
    }
  };

  const handleEnded = () => {
    endListenSession();
    setIsPlaying(false);
    if (activeQueue.length > 1) {
      goNext({ keepPlaying: true });
    }
  };

  const handleError = () => {
    endListenSession();
    setIsPlaying(false);
    setIsLoading(false);
    setErrorMessage('This audio item could not load. Use next to keep listening.');
  };

  const closeFeedbackSheet = useCallback(() => {
    setFeedbackOpen(false);
  }, []);

  const closeSupportSheet = useCallback(() => {
    setSupportMode(null);
  }, []);

  const openFeedbackSheet = () => {
    if (!feedbackEligible) return;
    setFeedbackOpen(true);
  };

  const goToLogin = () => {
    history.push(feedbackLoginHref);
  };

  const rememberArtistInviteClaimIntent = useCallback(() => {
    if (!artistInviteToken || !queryTrackId) return;
    writeArtistInviteClaimIntent({
      trackId: queryTrackId,
      token: artistInviteToken,
    });
  }, [artistInviteToken, queryTrackId]);

  const claimArtistInvite = useCallback(async () => {
    if (!artistInviteToken || !queryTrackId) return;
    if (!player?.token) {
      rememberArtistInviteClaimIntent();
      history.push(feedbackLoginHref);
      return;
    }

    setArtistClaimSaving(true);
    setArtistInviteError('');
    try {
      const response = await axiosConn.post(
        'hitlab/radio/artist-invite/claim',
        {
          trackId: queryTrackId,
          token: artistInviteToken,
        },
        {
          headers: {
            Authorization: `Bearer ${player.token}`,
          },
        },
      );
      const result = response?.data?.data || {};
      setArtistClaimResult(result);
      setArtistInviteStatus((current) => ({
        ...(current || {}),
        available: true,
        status: result.connected ? 'mapped' : 'claimed',
        mappedPlayerId: result.connected
          ? result.playerId || current?.mappedPlayerId || ''
          : current?.mappedPlayerId || '',
        claimedPlayerId: result.playerId || current?.claimedPlayerId || '',
        message: result.playerId
          ? result.connected
            ? `Connected. This record is now linked to @${result.playerId}.`
            : `Claim pending: @${result.playerId}`
          : result.connected
            ? 'Connected. This record is now linked to your JG Artist Profile.'
            : 'Claim pending.',
      }));
    } catch (err) {
      setArtistClaimResult(null);
      setArtistInviteError(
        getHitLabApiMessage(err, 'This artist invite could not be claimed.'),
      );
    } finally {
      setArtistClaimSaving(false);
    }
  }, [
    artistInviteToken,
    feedbackLoginHref,
    history,
    player?.token,
    queryTrackId,
    rememberArtistInviteClaimIntent,
  ]);

  useEffect(() => {
    if (
      !player?.token ||
      !artistInviteToken ||
      !queryTrackId ||
      artistInviteLoading ||
      artistClaimSaving ||
      artistClaimResult ||
      artistInviteStatus?.available !== true
    ) {
      return;
    }
    const intent = readArtistInviteClaimIntent();
    if (
      intent?.trackId !== queryTrackId ||
      String(intent?.token || '') !== artistInviteToken
    ) {
      return;
    }
    clearArtistInviteClaimIntent();
    void claimArtistInvite();
  }, [
    artistClaimResult,
    artistClaimSaving,
    artistInviteLoading,
    artistInviteStatus?.available,
    artistInviteToken,
    claimArtistInvite,
    player?.token,
    queryTrackId,
  ]);

  const openTipSupport = () => {
    if (!currentItem?.hasJgkSupportFields) return;
    setSupportMode('tip');
  };

  const openBuySupport = () => {
    if (!currentItem?.hasJgkSupportFields) return;
    setSupportMode('buy');
  };

  const copyShareUrl = useCallback(async () => {
    if (!trackShareUrl || !navigator.clipboard?.writeText) {
      throw new Error('Clipboard is unavailable.');
    }
    await navigator.clipboard.writeText(trackShareUrl);
    setShareMessage('Link copied.');
    setShareError('');
  }, [trackShareUrl]);

  const shareCurrentRecord = useCallback(async () => {
    if (!isTrack || !trackShareUrl) return;
    const payload = {
      title: `${currentTrackTitle} by ${currentArtistName}`,
      text: `Hear ${currentTrackTitle} by ${currentArtistName} in Joe Galaxy Radio Rotation inside JG Universe.`,
      url: trackShareUrl,
    };
    setShareMessage('');
    setShareError('');
    if (navigator.share) {
      try {
        await navigator.share(payload);
        setShareMessage('Share opened.');
        return;
      } catch (err) {
        if (isNativeShareCancel(err)) return;
      }
    }
    try {
      await copyShareUrl();
    } catch (err) {
      setShareMessage('');
      setShareError('Could not copy link. Please copy from your browser.');
    }
  }, [
    copyShareUrl,
    currentArtistName,
    currentTrackTitle,
    isTrack,
    trackShareUrl,
  ]);

  const updatePurchasedTrack = ({ mode, trackId, result }) => {
    if (mode !== 'buy' || !trackId) return;
    setCatalogQueue((currentQueue) =>
      (Array.isArray(currentQueue) ? currentQueue : []).map((item) => {
        const itemTrackId = String(item?.trackId || item?.id || '');
        if (itemTrackId !== trackId) return item;
        return {
          ...item,
          canBuyWithJgk: false,
          canBuySupportWithJgk: false,
          alreadyPurchased: true,
          downloadAvailable: Boolean(result?.downloadAvailable),
          buyUnavailableReason: 'Purchased.',
        };
      }),
    );
  };

  useEffect(() => {
    const nextTrackId = String(currentItem?.id || '').trim();
    const previousTrackId = previousPanelTrackIdRef.current;
    previousPanelTrackIdRef.current = nextTrackId;
    if (!previousTrackId || previousTrackId === nextTrackId) return;

    setFeedbackOpen(false);
    setSupportMode(null);
    setPlayCountInfoOpen(false);
    setShareMessage('');
    setShareError('');
  }, [currentItem?.id]);

  if (!activeQueue.length || activeTrackCount <= 0) {
    return (
      <main className="hitlab-page hitlab-radio-page">
        <GenericHelmet
          title="Joe Galaxy Radio Rotation | Preparing"
          description="Joe Galaxy Radio Rotation is preparing for launch."
          canonicalUrl={RADIO_PUBLIC_URL}
          ogUrl={RADIO_PUBLIC_URL}
        />
        <section className="hitlab-shell hitlab-radio-empty">
          <p className="hitlab-kicker">JG RADIO ROTATION</p>
          <h1>Joe Galaxy Radio Rotation is preparing for launch.</h1>
          <p>
            Approved records will appear here as the rotation opens.
          </p>
          <Link className="hitlab-secondary-link" to="/hitlab">
            Back to HitLab
          </Link>
        </section>
      </main>
    );
  }

  return (
    <main className="hitlab-page hitlab-radio-page">
      <GenericHelmet {...radioPageMeta} />

      <section className="hitlab-radio-hero">
        <div className="hitlab-shell hitlab-radio-shell">
          <div className="hitlab-radio-copy">
            <p className="hitlab-kicker">
              {isFocusedTrackView
                ? 'JOE GALAXY RADIO ROTATION'
                : 'JG RADIO ROTATION'}
            </p>
            <h1>
              {isFocusedTrackView
                ? `Hear "${currentTrackTitle}" by ${currentArtistName}`
                : 'Joe Galaxy Radio Rotation'}
            </h1>
            <p className="hitlab-lede">
              {isFocusedTrackView
                ? 'This record is in Joe Galaxy Radio Rotation inside JG Universe.'
                : 'Records are already in rotation inside JG Universe.'}
            </p>
            <p>
              {isFocusedTrackView
                ? 'Listen free. Sign in to leave feedback, tip with JGK, or buy/support the record with JGK.'
                : 'HitLab is the gate for artists with one original record strong enough to earn the next move. Placement cannot be bought.'}
            </p>
            {queryTrackMissing ? (
              <p className="hitlab-radio-query-alert" role="status">
                That shared record is not in the current Radio Rotation right now.
                Showing the live rotation.
              </p>
            ) : null}
            {isFocusedTrackView ? (
              <div className="hitlab-radio-focused-summary">
                <span>In Joe Galaxy Radio Rotation</span>
                <strong>{currentTrackTitle}</strong>
                <small>{currentArtistName}</small>
              </div>
            ) : null}
          </div>

          <article
            className={`hitlab-radio-player ${isCompact ? 'is-compact' : ''}`}
            aria-label="Joe Galaxy Radio Rotation player"
          >
            <div className="hitlab-radio-player-top">
              <div className="hitlab-radio-brand-lockup">
                <span className="hitlab-radio-brand-mark-shell" aria-hidden="true">
                  <img
                    className="hitlab-radio-brand-mark"
                    src={JG_LOGO_MARK_URL}
                    alt=""
                    width="44"
                    height="44"
                    decoding="async"
                  />
                </span>
                <span className="hitlab-radio-label">
                  Joe Galaxy Radio Rotation
                </span>
              </div>
              <button
                type="button"
                className="hitlab-radio-icon-btn"
                onClick={() => setIsCompact((value) => !value)}
                aria-label={isCompact ? 'Expand radio player' : 'Minimize radio player'}
              >
                {isCompact ? 'Expand' : 'Minimize'}
              </button>
            </div>

            <div className="hitlab-radio-now">
              <div className="hitlab-radio-art-wrap">
                {isTrack ? (
                  <img
                    className="hitlab-radio-cover"
                    src={currentItem.coverArtUrl}
                    alt={`Cover art for ${currentItem.songTitle} by ${currentItem.artistName}`}
                    width="420"
                    height="420"
                    decoding="async"
                  />
                ) : null}
                {isBumper ? (
                  <div
                    className="hitlab-radio-bumper-visual"
                    data-testid="hitlab-radio-bumper-visual"
                    aria-label="Station bumper visual"
                  >
                    <span>JG</span>
                    <strong>ON AIR</strong>
                  </div>
                ) : null}
              </div>

              <div className="hitlab-radio-details">
                <p className="hitlab-radio-state">
                  {isBumper ? 'Station sweep' : 'Now playing'}
                </p>
                <h2>{isTrack ? currentItem.songTitle : currentItem.label}</h2>
                <p className="hitlab-radio-artist">
                  {isTrack && artistPresenceClass ? (
                    <span
                      className={[
                        'hitlab-radio-presence-dot',
                        artistPresenceClass,
                      ]
                        .filter(Boolean)
                        .join(' ')}
                      aria-label={artistPresenceLabel}
                      title={artistPresenceLabel}
                      role="img"
                    />
                  ) : null}
                  <span>{isTrack ? currentItem.artistName : currentItem.title}</span>
                </p>
                {isTrack ? (
                  <span className="hitlab-radio-rotation-chip">
                    In Joe Galaxy Radio Rotation
                  </span>
                ) : null}
                {showLiveRoomState ? (
                  <div
                    className="hitlab-radio-live-room"
                    aria-label="JG Radio live room"
                  >
                    <RadioListenerPresenceRow
                      presence={radioLiveSignals.stationListenerPresence}
                      label="listening across JG Radio"
                      className="hitlab-radio-listener-row is-station"
                      avatarClassName="hitlab-radio-listener-avatar"
                      anonymousClassName="is-anonymous"
                      showAnonymousBubbles
                    />
                    {radioLiveSignals.movementText ? (
                      <span className="hitlab-radio-live-movement" role="status">
                        {radioLiveSignals.movementText}
                      </span>
                    ) : null}
                    {isTrack ? (
                      <RadioListenerPresenceRow
                        presence={radioLiveSignals.listenerPresence}
                        label="listening to this record"
                        className="hitlab-radio-listener-row"
                        avatarClassName="hitlab-radio-listener-avatar"
                        anonymousClassName="is-anonymous"
                        showAnonymousBubbles
                      />
                    ) : null}
                  </div>
                ) : null}
                {isTrack ? (
                  <div
                    className="hitlab-radio-track-stats-wrap"
                    onMouseLeave={() => setPlayCountInfoOpen(false)}
                  >
                    <div
                      className="hitlab-radio-track-stats"
                      aria-label={`${currentItem.songTitle} qualified plays`}
                    >
                      <HearingIcon aria-hidden="true" />
                      <span>{currentTrackPlayCount.toLocaleString()} plays</span>
                    </div>
                    <button
                      type="button"
                      className="hitlab-radio-play-info-btn"
                      aria-label="How plays are counted"
                      aria-expanded={playCountInfoOpen}
                      aria-describedby={
                        playCountInfoOpen
                          ? 'hitlab-radio-play-count-info'
                          : undefined
                      }
                      onClick={() => setPlayCountInfoOpen(true)}
                      onFocus={() => setPlayCountInfoOpen(true)}
                      onBlur={() => setPlayCountInfoOpen(false)}
                      onMouseEnter={() => setPlayCountInfoOpen(true)}
                    >
                      <InfoOutlinedIcon aria-hidden="true" />
                    </button>
                    {currentItem.isNewInRotation ? (
                      <span
                        className="hitlab-radio-new-badge"
                        title="Just added. Early spins are normal while this record starts its run."
                      >
                        NEW IN ROTATION
                      </span>
                    ) : null}
                    {playCountInfoOpen ? (
                      <div
                        id="hitlab-radio-play-count-info"
                        className="hitlab-radio-play-info-popover"
                        role="tooltip"
                      >
                        <strong>How plays are counted</strong>
                        <p>
                          A play counts after someone listens long enough: at
                          least 30 seconds or 25% of the record.
                        </p>
                        <p>
                          Logged-in JG Players count once per record per day.
                        </p>
                        <p>
                          Public HitLab preview listens can also count once per
                          visitor session per record per day.
                        </p>
                        <p>Bumpers do not count.</p>
                      </div>
                    ) : null}
                  </div>
                ) : null}
                <div
                  className={`hitlab-radio-visualizer ${isPlaying ? 'is-playing' : ''}`}
                  aria-hidden="true"
                >
                  <span />
                  <span />
                  <span />
                  <span />
                  <span />
                </div>
              </div>
            </div>

            <audio
              ref={audioRef}
              src={currentItem.audioUrl}
              preload="metadata"
              onLoadedMetadata={handleLoadedMetadata}
              onTimeUpdate={handleTimeUpdate}
              onEnded={handleEnded}
              onError={handleError}
              aria-label="Radio playback audio"
            />

            <div className="hitlab-radio-controls" aria-label="Radio controls">
              <button
                type="button"
                className="hitlab-radio-control-btn"
                onClick={goPrevious}
                aria-label="Previous radio item"
              >
                Previous
              </button>
              <button
                type="button"
                className="hitlab-radio-play-btn"
                onClick={togglePlayback}
                aria-label={isPlaying ? 'Pause radio rotation' : 'Play radio rotation'}
              >
                {isLoading ? 'Loading' : isPlaying ? 'Pause' : 'Play'}
              </button>
              <button
                type="button"
                className="hitlab-radio-control-btn"
                onClick={() => goNext()}
                aria-label="Next radio item"
              >
                Next
              </button>
            </div>

            <div className="hitlab-radio-progress-row">
              <span>{formatTime(elapsed)}</span>
              <div
                className="hitlab-radio-progress"
                role="progressbar"
                aria-label="Radio playback progress"
                aria-valuemin="0"
                aria-valuemax="100"
                aria-valuenow={Math.round(progress)}
              >
                <span style={{ width: `${progress}%` }} />
              </div>
              <span>{formatTime(duration)}</span>
            </div>

            {errorMessage ? (
              <div className="hitlab-radio-error" role="alert">
                {errorMessage}
              </div>
            ) : null}

            {showArtistInvitePanel ? (
              <section
                className="hitlab-radio-artist-claim-panel"
                aria-label="Artist invite claim"
              >
                <div>
                  <p className="hitlab-kicker">THIS IS YOUR RECORD?</p>
                  <h3>Claim & connect your JG Artist Profile.</h3>
                  <p>
                    Your record is live in Joe Galaxy Radio. Claim your JG
                    Artist Profile so this song can connect to you inside JG
                    Universe.
                  </p>
                  <p>
                    Once connected, listener feedback, JGK support, Buy /
                    Support, and future opportunities can route back to your
                    artist profile.
                  </p>
                </div>
                {artistInviteLoading ? (
                  <p className="hitlab-radio-artist-claim-status" role="status">
                    Checking invite...
                  </p>
                ) : null}
                {!artistInviteLoading && artistInviteStatus?.available ? (
                  <div className="hitlab-radio-artist-claim-actions">
                    {showArtistClaimSuccess ? (
                      <p className="hitlab-radio-artist-claim-status" role="status">
                        Connected. This record is now linked to{' '}
                        {artistClaimPlayerId
                          ? `@${artistClaimPlayerId}`
                          : 'your JG Artist Profile'}.
                      </p>
                    ) : showArtistClaimPending ? (
                      <p className="hitlab-radio-artist-claim-status" role="status">
                        Claim already pending for this record.
                      </p>
                    ) : player?.token ? (
                      <button
                        type="button"
                        className="hitlab-primary-btn"
                        onClick={claimArtistInvite}
                        disabled={artistClaimSaving}
                      >
                        {artistClaimSaving
                          ? 'Connecting'
                          : 'Claim & Connect Artist Profile'}
                      </button>
                    ) : (
                      <Link
                        className="hitlab-primary-btn"
                        to={feedbackLoginHref}
                        onClick={rememberArtistInviteClaimIntent}
                      >
                        Claim & Create JG Player
                      </Link>
                    )}
                    <Link className="hitlab-secondary-btn" to="/hitlab">
                      Create / complete your JG Artist Profile
                    </Link>
                  </div>
                ) : null}
                {!artistInviteLoading && artistInviteStatus?.available === false ? (
                  <p className="hitlab-radio-artist-claim-status" role="status">
                    {artistInviteStatus.message ||
                      'This artist invite is not available. You can still listen to the record.'}
                  </p>
                ) : null}
                {artistInviteError ? (
                  <p className="hitlab-radio-artist-claim-error" role="alert">
                    {artistInviteError}
                  </p>
                ) : null}
              </section>
            ) : null}

            {feedbackEligible ? (
              <div className="hitlab-radio-action-rail" aria-label="Fan actions">
                <button
                  type="button"
                  ref={feedbackCtaRef}
                  onClick={openFeedbackSheet}
                  aria-label="Give feedback and earn JGK"
                >
                  Give Feedback <span>Earn JGK</span>
                </button>
                {currentItem.canTipWithJgk ? (
                  <button
                    type="button"
                    onClick={openTipSupport}
                    aria-label="Tip with JGK"
                  >
                    Tip with JGK <span>Support artist</span>
                  </button>
                ) : currentItem.hasJgkSupportFields ? (
                  <button
                    type="button"
                    disabled
                    aria-label={`Tip with JGK unavailable: ${
                      currentItem.tipUnavailableReason ||
                      'Artist support is not set up for this record yet.'
                    }`}
                  >
                    Tip with JGK{' '}
                    <span>
                      {currentItem.tipUnavailableReason ||
                        'Artist support is not set up for this record yet.'}
                    </span>
                  </button>
                ) : (
                  <button type="button" disabled aria-label="Tip with JGK coming next">
                    Tip with JGK <span>Coming next</span>
                  </button>
                )}
                {currentItem.canBuySupportWithJgk ||
                (currentItem.alreadyPurchased && currentItem.hasJgkSupportFields) ? (
                  <button
                    type="button"
                    onClick={openBuySupport}
                    aria-label={
                      currentItem.alreadyPurchased
                        ? 'Open Buy or Support purchase status'
                        : 'Buy or Support with JGK'
                    }
                  >
                    {currentItem.alreadyPurchased ? 'Purchased' : 'Buy / Support'}
                    <span>
                      {currentItem.alreadyPurchased
                        ? currentItem.downloadAvailable
                          ? 'Download issued'
                          : 'Download used'
                        : currentItem.buyPriceJgk
                          ? `${currentItem.buyPriceJgk} JGK`
                          : 'Buy with JGK'}
                    </span>
                  </button>
                ) : currentItem.hasJgkSupportFields ? (
                  <button
                    type="button"
                    disabled
                    aria-label={`Buy or Support unavailable: ${
                      currentItem.buyUnavailableReason ||
                      'Artist support is not set up for this record yet.'
                    }`}
                  >
                    Buy / Support{' '}
                    <span>
                      {currentItem.buyUnavailableReason ||
                        'Artist support is not set up for this record yet.'}
                    </span>
                  </button>
                ) : (
                  <button
                    type="button"
                    disabled
                    aria-label="Buy or Support with JGK coming next"
                  >
                    Buy / Support with JGK <span>Coming next</span>
                  </button>
                )}
                {trackShareUrl ? (
                  <button
                    type="button"
                    onClick={shareCurrentRecord}
                    aria-label={`Share ${currentItem.songTitle}`}
                  >
                    Share record <span>Rich preview link</span>
                  </button>
                ) : null}
                {currentItem.artistInstagramUrl ? (
                  <a
                    href={currentItem.artistInstagramUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    aria-label={`Open ${currentItem.artistName} Instagram`}
                  >
                    Instagram
                  </a>
                ) : null}
              </div>
            ) : null}
            {shareMessage ? (
              <div className="hitlab-radio-share-message" role="status">
                {shareMessage}
              </div>
            ) : null}
            {shareError ? (
              <div className="hitlab-radio-share-message is-error" role="alert">
                {shareError}
              </div>
            ) : null}
          </article>
        </div>
      </section>

      <section className="hitlab-shell hitlab-radio-context" aria-label="Joe Galaxy Radio context">
        <div className="hitlab-radio-context-grid">
          <article className="hitlab-radio-context-card">
            <p className="hitlab-kicker">WHY THIS RECORD IS HERE</p>
            <h2>Selected for Joe Galaxy Radio Rotation</h2>
            <p>
              This record is active inside JG Universe because it cleared the
              basic rights and permission checks and was approved for Radio
              Rotation. Radio Rotation is earned. Placement cannot be bought.
            </p>
          </article>
          <article className="hitlab-radio-context-card">
            <p className="hitlab-kicker">WHAT YOU CAN DO</p>
            <h2>Listen first. Then act.</h2>
            <p>
              Anyone can listen. Sign in as a JG Player to leave feedback, tip
              with JGK, or buy/support the record with JGK and receive a
              one-time MP3 download.
            </p>
          </article>
          <article className="hitlab-radio-context-card">
            <p className="hitlab-kicker">ARE YOU AN ARTIST?</p>
            <h2>Bring one song you would bet on.</h2>
            <p>
              JG HitLab is where serious independent artists bring one original
              record into JG Universe. Get heard, receive a real Listen Result,
              and earn the next move.
            </p>
            <Link className="hitlab-secondary-btn" to="/hitlab">
              Start Artist Intake
            </Link>
          </article>
          <article className="hitlab-radio-context-card">
            <p className="hitlab-kicker">PART OF JG UNIVERSE</p>
            <h2>Music, players, support, and local opportunity.</h2>
            <p>
              JG Universe connects records, listeners, JGK support, and JG
              LocalOps. Artists can also become helpers or task creators as new
              regions open.
            </p>
            <Link className="hitlab-secondary-btn" to={feedbackLoginHref}>
              Sign in as JG Player
            </Link>
          </article>
        </div>
      </section>

      <HitLabRadioFeedbackSheet
        open={feedbackOpen && feedbackEligible}
        track={currentItem}
        player={player}
        elapsed={elapsed}
        duration={duration}
        source="joe_galaxy_radio"
        route={currentRoute}
        loginHref={feedbackLoginHref}
        triggerRef={feedbackCtaRef}
        onClose={closeFeedbackSheet}
        onLogin={goToLogin}
      />
      <HitLabRadioJgkSupportSheet
        open={Boolean(supportMode)}
        mode={supportMode || 'tip'}
        track={currentItem}
        player={player}
        route={currentRoute}
        loginHref={feedbackLoginHref}
        onClose={closeSupportSheet}
        onLogin={goToLogin}
        onSuccess={updatePurchasedTrack}
      />
    </main>
  );
}
